import { useNavigation } from "@react-navigation/native"
import { StackNavigationProp } from "@react-navigation/stack"
import {
  DateTimePicker,
  FormikTextInput,
  functions,
  logger,
  Subtitle1,
  useAlert,
  useTheme,
  VectorIcon,
  VectorIconProps,
} from "capsule"
import { Formik, FormikProps } from "formik"
import React, { FC, useCallback, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Platform, StyleSheet, View } from "react-native"
import { KeyboardAwareScrollView } from "react-native-keyboard-aware-scroll-view"
import * as Yup from "yup"

import SubmitButton from "../../components/SubmitButton"
import { ICON_SIZE, INPUT_HEIGHT, SCROLL_HEIGHT } from "../../features/config/Constants"
import { PractitionerHomeNS, ProfileNS } from "../../features/i18n/constants"
import { getUtcDate } from "../../utils/conversion"
import { ICodesParamList } from "../MainScreen/MainTabs"

type NavigationProp = StackNavigationProp<ICodesParamList>

const CHECK_ICON_SIZE = 40
const CHECK_CONTAINER_SIZE = 70
const SUCCESS_GREEN = "#4CAF50"

export interface PrescriptionLCAFormValues {
  lastName: string
  firstName: string
  email: string
  surgeryDate: Date | null
}

const initialValues: PrescriptionLCAFormValues = {
  lastName: "",
  firstName: "",
  email: "",
  surgeryDate: null,
}

const PrescriptionLCAScreen: FC = () => {
  const {
    fontMaker,
    colors: {
      surface,
      black: { mediumEmphasis },
      white: { highEmphasis: white },
    },
    dimensions: { spacing },
    screenStyle,
  } = useTheme()
  const { t } = useTranslation()
  const { showSnack } = useAlert()
  const navigation = useNavigation<NavigationProp>()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const currentDate = useMemo(() => getUtcDate(undefined, true), [])

  const validationSchema = useMemo(
    () =>
      Yup.object<PrescriptionLCAFormValues>({
        lastName: Yup.string().required(t(`${ProfileNS}:required.lastName`)),
        firstName: Yup.string().required(t(`${ProfileNS}:required.firstName`)),
        email: Yup.string()
          .email(t(`${ProfileNS}:wrongFormat.email`))
          .required(t(`${ProfileNS}:required.email`)),
        surgeryDate: Yup.date()
          .nullable()
          .required(t(`${PractitionerHomeNS}:prescriptionLCA.required.surgeryDate`)),
      }),
    [t],
  )

  const onSubmit = useCallback(
    async (values: PrescriptionLCAFormValues) => {
      setIsSubmitting(true)
      try {
        const generateLCACode = functions().httpsCallable("generateLCACode")
        const result = await generateLCACode({
          patientFirstName: values.firstName,
          patientLastName: values.lastName,
          patientEmail: values.email,
          plannedSurgeryDate: values.surgeryDate?.getTime(),
        })
        const generatedCode = (result.data as { success: boolean; code: string }).code
        navigation.navigate("PrescriptionLCAConfirmation", { code: generatedCode })
      } catch (error) {
        logger("Error creating LCA code:", error)
        showSnack({ message: t(`${PractitionerHomeNS}:prescriptionLCA.error`) })
      } finally {
        setIsSubmitting(false)
      }
    },
    [showSnack, t, navigation],
  )

  const s = useMemo(
    () => ({
      content: [
        styles.content,
        {
          paddingTop: spacing,
          backgroundColor: surface.background,
        },
      ],
      innerContent: {
        paddingBottom: spacing * 2,
      },
      inputContainer: {
        paddingHorizontal: spacing,
        backgroundColor: surface.background,
      },
      child: { paddingBottom: spacing },
      input: [
        styles.input,
        {
          paddingLeft: spacing / 4,
        },
      ],
      dateInput: [styles.input, { paddingLeft: spacing / 2, backgroundColor: surface.textInput }],
      headerContainer: [
        styles.headerContainer,
        {
          marginBottom: spacing * 2,
        },
      ],
      checkContainer: [
        styles.checkContainer,
        {
          marginBottom: spacing * 1.5,
        },
      ],
      introText: [
        fontMaker({ weight: "Bold" }),
        {
          marginBottom: spacing,
        },
      ],
      descriptionText: {
        marginBottom: spacing,
      },
    }),
    [spacing, surface.textInput, surface.background, fontMaker],
  )

  const renderIcon = useCallback(
    (name: string) =>
      ({
        name,
        category: "MaterialIcons",
        size: ICON_SIZE,
        color: mediumEmphasis,
      } as VectorIconProps),
    [mediumEmphasis],
  )

  const renderForm = useCallback(
    ({ values }: FormikProps<PrescriptionLCAFormValues>) => (
      <View style={s.inputContainer}>
        <View style={s.headerContainer}>
          <View style={s.checkContainer}>
            <VectorIcon
              name="check"
              category="MaterialIcons"
              size={CHECK_ICON_SIZE}
              color={white}
            />
          </View>
          <Subtitle1 emphasis="high" style={s.introText}>
            {t(`${PractitionerHomeNS}:prescriptionLCA.intro`)}
          </Subtitle1>
          <Subtitle1 style={s.descriptionText}>
            {t(`${PractitionerHomeNS}:prescriptionLCA.description`)}
          </Subtitle1>
        </View>
        <FormikTextInput
          style={s.input}
          name="lastName"
          autoComplete="off"
          autoCorrect={false}
          textContentType="none"
          spellCheck={false}
          styles={{ child: s.child }}
          label={t("fields.label.lastName")}
        />
        <FormikTextInput
          style={s.input}
          name="firstName"
          autoComplete="off"
          autoCorrect={false}
          textContentType="none"
          spellCheck={false}
          styles={{ child: s.child }}
          label={t("fields.label.firstName")}
        />
        <FormikTextInput
          style={s.input}
          name="email"
          autoComplete="off"
          autoCorrect={false}
          textContentType="none"
          spellCheck={false}
          keyboardType="email-address"
          autoCapitalize="none"
          styles={{ child: s.child }}
          label={t(`${PractitionerHomeNS}:prescriptionLCA.fields.email`)}
        />
        <DateTimePicker
          mode="date"
          name="surgeryDate"
          styles={{ child: s.child }}
          touchableStyle={s.dateInput}
          label={t(`${PractitionerHomeNS}:prescriptionLCA.fields.surgeryDate`)}
          icons={{ left: renderIcon("calendar-today"), right: renderIcon("arrow-drop-down") }}
          initialValue={values.surgeryDate ?? currentDate}
          noAdditionalValue
        />
        <SubmitButton<PrescriptionLCAFormValues>
          label={t(`${PractitionerHomeNS}:prescriptionLCA.button.validate`)}
          syncLoading={isSubmitting}
        />
      </View>
    ),
    [s, t, renderIcon, currentDate, white, isSubmitting],
  )

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      validateOnMount
    >
      {formikProps => (
        <KeyboardAwareScrollView
          style={[s.content, screenStyle]}
          enableOnAndroid
          keyboardShouldPersistTaps="handled"
          contentContainerStyle={s.innerContent}
          extraScrollHeight={Platform.OS === "ios" ? SCROLL_HEIGHT : undefined}
        >
          {renderForm(formikProps)}
        </KeyboardAwareScrollView>
      )}
    </Formik>
  )
}

const styles = StyleSheet.create({
  content: {
    flex: 1,
  },
  input: {
    height: INPUT_HEIGHT,
    minHeight: INPUT_HEIGHT,
    borderRadius: 0,
  },
  headerContainer: {
    alignItems: "stretch",
  },
  checkContainer: {
    width: CHECK_CONTAINER_SIZE,
    height: CHECK_CONTAINER_SIZE,
    borderRadius: CHECK_CONTAINER_SIZE / 2,
    backgroundColor: SUCCESS_GREEN,
    justifyContent: "center",
    alignItems: "center",
    alignSelf: "center",
  },
})

export default PrescriptionLCAScreen
